// #18 Select: a native <select> in the .mc-inp box with a chevron (the browser's own list, keyboard and screen-reader
// behaviour). Inside a Field it takes the Field's id and wiring. An empty value shows "—" in --text-3 (.mc-sel-empty);
// no options at all is the empty state, "—" alone. loading: a skeleton at the control's geometry (the option list is
// not known yet). blocked (DC-24): the values-only view — the chosen option's words as a read-only field, aria-disabled,
// the inline reason and the mirrored tooltip; outside a Field it keeps the caller's aria-label / aria-labelledby as its name. mode="view": the chosen words as text.
// Per-option state (a room at a slot): an option's `state` is free or taken. A native <option> holds no mark, so a taken
// option is disabled and its reason is read through the select's description (one .mc-sr line naming each taken option
// and why); the box draws the chosen option's dot, green when free and neutral otherwise (decorative, the words carry it).

import { forwardRef, useId, type ChangeEvent, type SelectHTMLAttributes } from "react";
import { cx } from "../lib/cx";
import { useHs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { type ControlProps } from "./Button";
import { FIELD_NONE, useFieldControl } from "./Field";
import { Icon } from "./icons";
import { Skeleton } from "./Skeleton";
import { VisuallyHidden } from "../lib/VisuallyHidden";

export interface SelectOption {
  value: string;
  /** The option's words: a reason from the Admin's configuration, a person's name, a status. */
  label: string;
  disabled?: boolean | undefined;
  /** Free or taken at the moment the choice is for (a room at a slot). Taken is disabled. */
  state?: "free" | "taken" | undefined;
  /** Why a taken option cannot be chosen (the caller's words, e.g. *busy*); read in the select's description. */
  reason?: string | undefined;
}

type NativeSelect = Omit<SelectHTMLAttributes<HTMLSelectElement>, "style" | "className" | "disabled" | "children" | "onChange" | "value" | "defaultValue" | "multiple" | "size">;

export interface SelectProps extends ControlProps, NativeSelect {
  options: readonly SelectOption[];
  value?: string | undefined;
  defaultValue?: string | undefined;
  /** Receives the chosen value. */
  onChange?: ((value: string, event: ChangeEvent<HTMLSelectElement>) => void) | undefined;
  /** The empty choice's words (default "—"); always offered unless `required` and a value is chosen. */
  placeholder?: string | undefined;
  sm?: boolean | undefined;
  loading?: boolean | undefined;
  invalid?: boolean | undefined;
  className?: string | undefined;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    options, value, defaultValue, onChange, placeholder = FIELD_NONE, sm, loading, invalid, can, blocked, mode, hs, className, id,
    "aria-describedby": describedBy, ...rest
  },
  ref,
) {
  const hot = useHs(hs);
  const takenId = `${useId()}-taken`;
  const taken = options.filter((option) => option.state === "taken");
  const stated = options.some((option) => option.state !== undefined);
  const describedByAll = [describedBy, taken.length > 0 && !loading ? takenId : undefined].filter(Boolean).join(" ") || undefined;
  const control = useFieldControl({ id, invalid, blocked, mode, "aria-describedby": describedByAll, required: rest.required });
  if (can === false) return null;
  const chosen = value ?? defaultValue ?? "";
  const picked = options.find((option) => option.value === chosen);
  const takenWords = taken.map((option) => (option.reason ? `${option.label}: ${option.reason}` : option.label)).join("; ");
  const words = picked?.label;
  if (control.mode === "view") return <span className="mc-field-v">{words ?? FIELD_NONE}</span>;
  if (loading) {
    return <span className={cx("mc-sel-skel", sm && "mc-inp-sm")} aria-busy="true"><Skeleton shape="input" height={sm ? 34 : 40} /></span>;
  }
  if (control.blocked) {
    return (
      <>
        <Tooltip label={control.blocked.reason} mode="mirror">
          <span className={cx("mc-inp", "mc-sel", sm && "mc-inp-sm", "mc-dis", className)}>
            <input aria-label={rest["aria-label"]} aria-labelledby={rest["aria-labelledby"]} {...control.aria} {...hot} id={control.id} className="mc-inp-c" readOnly value={words ?? FIELD_NONE} />
          </span>
        </Tooltip>
        {control.reason}
        {taken.length > 0 ? <VisuallyHidden id={takenId}>{takenWords}</VisuallyHidden> : null}
      </>
    );
  }
  const empty = options.length === 0;
  return (
    <span className={cx("mc-inp", "mc-sel", sm && "mc-inp-sm", control.invalid && "mc-inp-err", (chosen === "" || empty) && "mc-sel-empty", stated && "mc-sel-st", className)}>
      {stated ? <span className={cx("mc-sel-dot", picked?.state === "free" && "mc-sel-free")} aria-hidden="true" /> : null}
      <select
        {...rest}
        {...control.aria}
        {...hot}
        ref={ref}
        id={control.id}
        className="mc-inp-c"
        value={value}
        defaultValue={value === undefined ? defaultValue ?? "" : undefined}
        onChange={(event) => onChange?.(event.target.value, event)}
      >
        {rest.required && chosen !== "" ? null : <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled || option.state === "taken"}>{option.label}</option>
        ))}
      </select>
      <Icon name="down" size={16} />
      {taken.length > 0 ? <VisuallyHidden id={takenId}>{takenWords}</VisuallyHidden> : null}
    </span>
  );
});
